import React, { FC, useMemo } from 'react';
import { Table, TableBody, TableHead, TableRow, Box, Tooltip } from '@mui/material';
import { DragDropContext, Droppable, DropResult } from '@hello-pangea/dnd';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { DataTableCell, DataTableHeadCell } from 'components/shared/data-table/data-table.componet';
import InfoText from 'components/shared/info-text.component';
import ProductionSchedulingRow from './production-scheduling-row.component';
import { DepartmentCapacityEntry, ProductionPlanningStep } from 'models/production-planning';
import { useDepartments } from 'context/departments-context';
import { departmentCapacityTemplate } from 'apollo/templates/order/query.graphql';
import { machinesTemplate } from 'apollo/templates/organization/query.graphql';
import { Machine } from 'models/organization';
import { countWeekdaysFromStart } from '../production-scheduling.utils';

// Columns use fixed widths (table-layout: fixed) sized to their values; headings that are
// wider than their column are truncated with an ellipsis (full text stays in the tooltip).
const ELLIPSIS_HEADER_SX = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
} as const;

type ProductionSchedulingTableProps = {
    steps: ProductionPlanningStep[];
    orderId?: string;
    onDragEnd: (result: DropResult) => void;
    onStepChange: (index: number, update: Partial<ProductionPlanningStep>) => Promise<void>;
    onDeleteStep: (index: number) => void;
    onMoveStepToNextDay: (index: number) => Promise<void>;
    isDeadlineInvalid: (deadline: Date | null) => boolean;
    isStartDateInvalid: (step: ProductionPlanningStep, index: number) => boolean;
    isTimeInputInvalid: (index: number, field: 'production' | 'idle') => boolean;
    onTimeInputValidChange: (index: number, field: 'production' | 'idle', isValid: boolean) => void;
};

const ProductionSchedulingTable: FC<ProductionSchedulingTableProps> = ({
    steps,
    orderId,
    onDragEnd,
    onStepChange,
    onDeleteStep,
    onMoveStepToNextDay,
    isDeadlineInvalid,
    isStartDateInvalid,
    isTimeInputInvalid,
    onTimeInputValidChange,
}) => {
    const { t: translate } = useTranslation(['orders', 'common', 'lab-settings']);
    const { departments, getDepartmentNameForStep } = useDepartments();

    // Query department capacity data excluding the current order to avoid double-counting
    const { data: capacityData } = useQuery(departmentCapacityTemplate, {
        fetchPolicy: 'cache-and-network',
        variables: { excludeOrderId: orderId },
    });

    const { data: machinesData } = useQuery<{ machines: Machine[] }>(machinesTemplate);
    const machines = machinesData?.machines ?? [];

    // Build scheduled hours map per department per date from other orders
    const departmentHoursByDate = useMemo(() => {
        const entries = (capacityData?.departmentCapacity || []) as DepartmentCapacityEntry[];
        const map = new Map<string, number>();
        entries.forEach((entry) => {
            map.set(`${entry.department}-${entry.date}`, entry.totalHours);
        });
        return map;
    }, [capacityData]);

    // Get department limits. DD-3589: read from DepartmentsProvider, which already
    // fetches departments (and skips them for users the backend refuses).
    const departmentLimits = useMemo(() => {
        const limitsByName = new Map<string, number | undefined>();

        departments.forEach((dept) => {
            limitsByName.set(dept.name, dept.productionHourLimit);
        });

        return limitsByName;
    }, [departments]);

    // Build current order's hours per department per date
    const currentOrderHoursByDeptDate = useMemo(() => {
        const map = new Map<string, number>();
        for (const step of steps) {
            const deptName = getDepartmentNameForStep(step.stepId || '');
            const stepDate = step.startDate || step.deadline;
            if (!deptName || !stepDate) continue;
            const key = `${deptName}-${new Date(stepDate).toDateString()}`;
            map.set(key, (map.get(key) || 0) + (step.estimatedProductionHours || 0));
        }
        return map;
    }, [steps, getDepartmentNameForStep]);

    return (
        <DragDropContext onDragEnd={onDragEnd}>
            <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                <TableHead>
                    <TableRow>
                        <DataTableHeadCell width={32}></DataTableHeadCell>
                        <DataTableHeadCell sx={ELLIPSIS_HEADER_SX}>{translate('common:STEP')}</DataTableHeadCell>
                        <DataTableHeadCell width={60} sx={ELLIPSIS_HEADER_SX}>
                            <Tooltip title={translate('orders:PRODUCTION_HOURS_TOOLTIP')}>
                                <span>{translate('orders:PRODUCTION_HOURS')}</span>
                            </Tooltip>
                        </DataTableHeadCell>
                        <DataTableHeadCell width={140} sx={ELLIPSIS_HEADER_SX}>
                            <Tooltip title={translate('lab-settings:MACHINE_TOOLTIP')}>
                                <span>{translate('lab-settings:MACHINE')}</span>
                            </Tooltip>
                        </DataTableHeadCell>
                        <DataTableHeadCell width={60} sx={ELLIPSIS_HEADER_SX}>
                            <Tooltip title={translate('orders:MACHINE_HOURS_TOOLTIP')}>
                                <span>{translate('orders:MACHINE_HOURS')}</span>
                            </Tooltip>
                        </DataTableHeadCell>
                        <DataTableHeadCell width={60} sx={ELLIPSIS_HEADER_SX}>
                            <Tooltip title={translate('orders:TOTAL_DEPT_HOURS_TOOLTIP')}>
                                <span>{translate('orders:TOTAL_DEPT_HOURS')}</span>
                            </Tooltip>
                        </DataTableHeadCell>
                        <DataTableHeadCell width={170} sx={ELLIPSIS_HEADER_SX}>
                            {translate('orders:START_DATE')}
                        </DataTableHeadCell>
                        <DataTableHeadCell width={170} sx={ELLIPSIS_HEADER_SX}>
                            {translate('orders:DEADLINE')}
                        </DataTableHeadCell>
                        <DataTableHeadCell width={48} align="center" sx={ELLIPSIS_HEADER_SX}>
                            <Tooltip title={translate('orders:ALLOW_OVERNIGHT_TOOLTIP')}>
                                <span>{translate('orders:ALLOW_OVERNIGHT_ABBR')}</span>
                            </Tooltip>
                        </DataTableHeadCell>
                        <DataTableHeadCell width={48} align="center" sx={ELLIPSIS_HEADER_SX}>
                            <Tooltip title={translate('orders:START_NEXT_DAY_TOOLTIP')}>
                                <span>{translate('orders:START_NEXT_DAY_ABBR')}</span>
                            </Tooltip>
                        </DataTableHeadCell>
                        <DataTableHeadCell width={48} align="center" sx={ELLIPSIS_HEADER_SX}>
                            {translate('orders:DAY')}#
                        </DataTableHeadCell>
                        <DataTableHeadCell width={48}></DataTableHeadCell>
                    </TableRow>
                </TableHead>
                <Droppable
                    droppableId="production-scheduling-steps"
                    renderClone={(provided, _snapshot, rubric) => {
                        const row = steps[rubric.source.index];
                        const deptName = getDepartmentNameForStep(row?.stepId || '');
                        const label = deptName ? `${deptName} - ${row.stepName}` : row.stepName;
                        return (
                            <Box
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    background: '#e0e0e0',
                                    borderRadius: 1,
                                    padding: '8px 16px',
                                    fontSize: '0.9rem',
                                    boxShadow: 2,
                                }}
                            >
                                <DragIndicatorIcon fontSize="small" />
                                {label}
                            </Box>
                        );
                    }}
                >
                    {(provided) => (
                        <TableBody ref={provided.innerRef} {...provided.droppableProps}>
                            {steps.length > 0 ? (
                                steps.map((row, index) => {
                                    const deptName = getDepartmentNameForStep(row.stepId || '');
                                    const firstStepStart = steps[0]?.startDate;
                                    const dayNumber =
                                        row.dayNumber !== undefined
                                            ? row.dayNumber
                                            : firstStepStart && row.deadline
                                              ? countWeekdaysFromStart(new Date(firstStepStart), new Date(row.deadline))
                                              : index + 1;

                                    // Calculate total department hours for this specific date
                                    // = other orders' hours + all current order's steps in same dept+date
                                    const stepDate = row.startDate || row.deadline;
                                    const dateKey = stepDate ? new Date(stepDate).toDateString() : '';
                                    const key = `${deptName}-${dateKey}`;
                                    const otherOrdersHours = departmentHoursByDate.get(key) || 0;
                                    const currentOrderHours = currentOrderHoursByDeptDate.get(key) || 0;
                                    const totalDepartmentHours = otherOrdersHours + currentOrderHours;
                                    const departmentLimit = departmentLimits.get(deptName);
                                    const isOverLimit =
                                        departmentLimit !== undefined &&
                                        departmentLimit > 0 &&
                                        totalDepartmentHours > departmentLimit;

                                    return (
                                        <ProductionSchedulingRow
                                            key={row.stepId || index}
                                            row={row}
                                            index={index}
                                            departmentName={deptName}
                                            dayNumber={dayNumber}
                                            totalDepartmentHours={totalDepartmentHours}
                                            isOverLimit={isOverLimit}
                                            isStartDateInvalid={isStartDateInvalid(row, index)}
                                            isDeadlineInvalid={isDeadlineInvalid(row.deadline)}
                                            isProductionHoursInvalid={isTimeInputInvalid(index, 'production')}
                                            isMachineHoursInvalid={isTimeInputInvalid(index, 'idle')}
                                            machines={machines}
                                            onStepChange={onStepChange}
                                            onDeleteStep={onDeleteStep}
                                            onMoveStepToNextDay={onMoveStepToNextDay}
                                            onProductionHoursValidChange={(idx, valid) =>
                                                onTimeInputValidChange(idx, 'production', valid)
                                            }
                                            onMachineHoursValidChange={(idx, valid) =>
                                                onTimeInputValidChange(idx, 'idle', valid)
                                            }
                                        />
                                    );
                                })
                            ) : (
                                <TableRow>
                                    <DataTableCell colSpan={12} style={{ padding: 4 }}>
                                        <InfoText text={translate('orders:NO_STEPS_ADDED_YET')} />
                                    </DataTableCell>
                                </TableRow>
                            )}
                            {provided.placeholder}
                        </TableBody>
                    )}
                </Droppable>
            </Table>
        </DragDropContext>
    );
};

export default ProductionSchedulingTable;
